import { ConverterMap } from './shared';
import { maybeUnwrapValue, maybeWrapValue } from './criteria';
import { SearchOperator as Op } from '@fullstory/client-api';
import * as helpers from './helpers';
import {
  Browser,
  Device,
  DurationUnit,
  OS,
  Platform,
  SearchFilter,
  SearchType,
  PinchGesture,
  NONE,
  SeenVisibility,
  ErrorClickType,
  DatalossType,
  DependentType,
  isUnifiedDependentVisitedUrl,
  isUnifiedReferrerUrl,
  isUnifiedWebSourceFileUrl,
  isAjaxRequestUrl,
  UiOnlyKinds,
  UiOnlySubkinds,
} from '@fullstory/search-types';
import { FieldScope, VarType } from '@fullstory/search-vars';
import * as segment from '@fullstory/proto/fs/api/segment';
import * as customvars from './customvars';
import { dateConverter, elementLikeConverter } from './helpers';

export type DependentConverterMap = ConverterMap<DependentType>;

const city = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.city
>(segment.DependentEventFilter.filter_keys.city, 'PageCity', 0);

const region = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.region
>(segment.DependentEventFilter.filter_keys.region, 'PageRegion', 0);

const country = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.country
>(segment.DependentEventFilter.filter_keys.country, 'PageCountry', 0);

const email = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.email
>(segment.DependentEventFilter.filter_keys.email, 'UserEmail', 0);

const name = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.name
>(segment.DependentEventFilter.filter_keys.name, 'UserDisplayName', 0);

const userId = helpers.stringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.userId
>();

const userAgent = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.userAgent
>(segment.DependentEventFilter.filter_keys.userAgent, 'PageAgent', 0);

const requestMethod = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.requestMethod
>(segment.DependentEventFilter.filter_keys.requestMethod, 'ReqMethod', 0);

const statusCode = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.statusCode
>(segment.DependentEventFilter.filter_keys.statusCode, 'ReqStatus', 0);

const scrollDepth = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.scrollDepth
>(segment.DependentEventFilter.filter_keys.scrollDepth, 'PageMaxScrollDepthPercent', 0);

const appViewName = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.appViewName
>(segment.DependentEventFilter.filter_keys.appViewName, 'AppViewName', 0);

const appPackageName = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.appPackageName
>(segment.DependentEventFilter.filter_keys.appPackageName, 'AppPackageName', 0, undefined, false);

const appName = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.appName
>(segment.DependentEventFilter.filter_keys.appName, 'AppName', 0, undefined, false);

const deviceModel = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.deviceModel
>(segment.DependentEventFilter.filter_keys.deviceModel, 'AppDeviceModel', 0);

const deviceVendor = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.deviceVendor
>(segment.DependentEventFilter.filter_keys.deviceVendor, 'AppDeviceVendor', 0, undefined, false);

const ipAddress = helpers.stringRangeConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.ipAddress
>(segment.DependentEventFilter.filter_keys.ipAddress, 'PageIp', 0);

const pageName = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageName
>(segment.DependentEventFilter.filter_keys.pageName, 'PageName', 0);

const pageDuration = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageDuration
>(segment.DependentEventFilter.filter_keys.pageDuration, 'PageDuration', 0, DurationUnit.milli);

const pageActiveDuration = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageActiveDuration
>(
  segment.DependentEventFilter.filter_keys.pageActiveDuration,
  'PageActiveDuration',
  0,
  DurationUnit.milli,
);

const timeSincePageLoad = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.timeSincePageLoad
>(
  segment.DependentEventFilter.filter_keys.timeSincePageLoad,
  'EventPageOffset',
  0,
  DurationUnit.milli,
);

const timeSinceSessionStart = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.timeSinceSessionStart
>(
  segment.DependentEventFilter.filter_keys.timeSinceSessionStart,
  'EventSessionOffset',
  0,
  DurationUnit.milli,
);

const timeDurationRendered = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.timeDurationRendered
>(
  segment.DependentEventFilter.filter_keys.timeDurationRendered,
  'EventDurationMillis',
  0,
  DurationUnit.milli,
);

const timeDurationVisible = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.timeDurationVisible
>(
  segment.DependentEventFilter.filter_keys.timeDurationVisible,
  'EventSecondaryDurationMillis',
  0,
  DurationUnit.milli,
);

const consoleErrorsOnPage = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.consoleErrorsOnPage
>(segment.DependentEventFilter.filter_keys.consoleErrorsOnPage, 'PageNumErrors', 0);

const consoleWarningsOnPage = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.consoleWarningsOnPage
>(segment.DependentEventFilter.filter_keys.consoleWarningsOnPage, 'PageNumWarnings', 0);

const displayWidth = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.displayWidth
>(segment.DependentEventFilter.filter_keys.displayWidth, 'PageScreenWidth', 0);

const displayHeight = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.displayHeight
>(segment.DependentEventFilter.filter_keys.displayHeight, 'PageScreenHeight', 0);

const viewportWidth = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.viewportWidth
>(segment.DependentEventFilter.filter_keys.viewportWidth, 'PageViewportWidth', 0);

const viewportHeight = helpers.numericConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.viewportHeight
>(segment.DependentEventFilter.filter_keys.viewportHeight, 'PageViewportHeight', 0);

const referrerUrl = helpers.urlConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.referrerUrl
>(
  segment.DependentEventFilter.filter_keys.referrerUrl,
  'PageRefererUrl',
  0,
  s => isUnifiedReferrerUrl(s),
  helpers.referrerUrlCategoryToField,
  helpers.fieldToReferrerUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeReferrerUrl,
);

const url = helpers.urlConverter<DependentType, segment.DependentEventFilter.filter_keys.url>(
  segment.DependentEventFilter.filter_keys.url,
  'PageUrl',
  0,
  s => isUnifiedDependentVisitedUrl(s),
  helpers.visitedUrlCategoryToField,
  helpers.fieldToVisitedUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeDependentUrl,
);

const ajaxRequestUrl = helpers.urlConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.ajaxRequestUrl
>(
  segment.DependentEventFilter.filter_keys.ajaxRequestUrl,
  'ReqUrl',
  0,
  s => isAjaxRequestUrl(s),
  helpers.ajaxUrlCategoryToField,
  helpers.fieldToAjaxUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeAjaxVisitedUrl,
);

const webSourceFileUrl = helpers.urlConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.webSourceFileUrl
>(
  segment.DependentEventFilter.filter_keys.webSourceFileUrl,
  'EventWebSourceFileUrl',
  0,
  s => isUnifiedWebSourceFileUrl(s),
  helpers.webSourceFileUrlCategoryToField,
  helpers.fieldToWebSourceFileUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeWebSourceFileUrl,
);

const mobileSourceFile = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.mobileSourceFile
>(segment.DependentEventFilter.filter_keys.mobileSourceFile, 'EventMobileSourceFile', 0);

const appVersion = helpers.stringRangeConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.appVersion
>(segment.DependentEventFilter.filter_keys.appVersion, 'AppVersion', 0);

const appOsVersion = helpers.stringRangeConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.appOsVersion
>(segment.DependentEventFilter.filter_keys.appOsVersion, 'AppOsVersion', 0);

const appFsVersion = helpers.stringRangeConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.appFsVersion
>(segment.DependentEventFilter.filter_keys.appFsVersion, 'AppFsVersion', 0);

const browserVersion = helpers.stringRangeConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.browserVersion
>(segment.DependentEventFilter.filter_keys.browserVersion, 'BrowserVersion', 0);

const customApiErrorProperty = helpers.legacyStringOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.customApiErrorProperty
>(
  segment.DependentEventFilter.filter_keys.customApiErrorProperty,
  'EventVarFields',
  0,
  undefined,
  false,
);

const pageDefId = helpers.pageIdConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageDefId
>();

const prevPageDefId = helpers.pageIdConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.prevPageDefId
>();

const sessionEntryPage = helpers.boolConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.sessionEntryPage
>();

const sessionExitPage = helpers.boolConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.sessionExitPage
>();

const userVarString = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarString
>(segment.DependentEventFilter.filter_keys.userVarString, FieldScope.User, VarType.Str);

const userVarStrings = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarStrings
>(segment.DependentEventFilter.filter_keys.userVarStrings, FieldScope.User, VarType.Strs);

const userVarInt = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarInt
>(segment.DependentEventFilter.filter_keys.userVarInt, FieldScope.User, VarType.Int);

const userVarInts = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarInts
>(segment.DependentEventFilter.filter_keys.userVarInts, FieldScope.User, VarType.Ints);

const userVarFloat = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarFloat
>(segment.DependentEventFilter.filter_keys.userVarFloat, FieldScope.User, VarType.Real);

const userVarFloats = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarFloats
>(segment.DependentEventFilter.filter_keys.userVarFloats, FieldScope.User, VarType.Reals);

const userVarDate = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarDate
>(segment.DependentEventFilter.filter_keys.userVarDate, FieldScope.User, VarType.Date);

const userVarDates = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarDates
>(segment.DependentEventFilter.filter_keys.userVarDates, FieldScope.User, VarType.Dates);

const userVarBool = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarBool
>(segment.DependentEventFilter.filter_keys.userVarBool, FieldScope.User, VarType.Bool);

const userVarBools = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.userVarBools
>(segment.DependentEventFilter.filter_keys.userVarBools, FieldScope.User, VarType.Bools);

const fsUserId = helpers.legacyNumericIdConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsUserId
>(segment.DependentEventFilter.filter_keys.fsUserId, 'UserId', 0);

const fsPageClusterId = helpers.legacyNumericIdConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsPageClusterId
>(segment.DependentEventFilter.filter_keys.fsPageClusterId, 'PageClusterId', 0);

const customEventPropertyString = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyString
>(
  segment.DependentEventFilter.filter_keys.customEventPropertyString,
  FieldScope.Event,
  VarType.Str,
);

const customEventPropertyStrings = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyStrings
>(
  segment.DependentEventFilter.filter_keys.customEventPropertyStrings,
  FieldScope.Event,
  VarType.Strs,
);

const customEventPropertyInt = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyInt
>(segment.DependentEventFilter.filter_keys.customEventPropertyInt, FieldScope.Event, VarType.Int);

const customEventPropertyInts = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyInts
>(segment.DependentEventFilter.filter_keys.customEventPropertyInts, FieldScope.Event, VarType.Ints);

const customEventPropertyFloat = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyFloat
>(
  segment.DependentEventFilter.filter_keys.customEventPropertyFloat,
  FieldScope.Event,
  VarType.Real,
);

const customEventPropertyFloats = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyFloats
>(
  segment.DependentEventFilter.filter_keys.customEventPropertyFloats,
  FieldScope.Event,
  VarType.Reals,
);

const customEventPropertyDate = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyDate
>(segment.DependentEventFilter.filter_keys.customEventPropertyDate, FieldScope.Event, VarType.Date);

const customEventPropertyDates = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyDates
>(
  segment.DependentEventFilter.filter_keys.customEventPropertyDates,
  FieldScope.Event,
  VarType.Dates,
);

const customEventPropertyBool = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyBool
>(segment.DependentEventFilter.filter_keys.customEventPropertyBool, FieldScope.Event, VarType.Bool);

const customEventPropertyBools = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.customEventPropertyBools
>(
  segment.DependentEventFilter.filter_keys.customEventPropertyBools,
  FieldScope.Event,
  VarType.Bools,
);

const pageVarString = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarString
>(segment.DependentEventFilter.filter_keys.pageVarString, FieldScope.Page, VarType.Str);

const pageVarStrings = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarStrings
>(segment.DependentEventFilter.filter_keys.pageVarStrings, FieldScope.Page, VarType.Strs);

const pageVarInt = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarInt
>(segment.DependentEventFilter.filter_keys.pageVarInt, FieldScope.Page, VarType.Int);

const pageVarInts = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarInts
>(segment.DependentEventFilter.filter_keys.pageVarInts, FieldScope.Page, VarType.Ints);

const pageVarFloat = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarFloat
>(segment.DependentEventFilter.filter_keys.pageVarFloat, FieldScope.Page, VarType.Real);

const pageVarFloats = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarFloats
>(segment.DependentEventFilter.filter_keys.pageVarFloats, FieldScope.Page, VarType.Reals);

const pageVarDate = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarDate
>(segment.DependentEventFilter.filter_keys.pageVarDate, FieldScope.Page, VarType.Date);

const pageVarDates = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarDates
>(segment.DependentEventFilter.filter_keys.pageVarDates, FieldScope.Page, VarType.Dates);

const pageVarBool = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarBool
>(segment.DependentEventFilter.filter_keys.pageVarBool, FieldScope.Page, VarType.Bool);

const pageVarBools = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageVarBools
>(segment.DependentEventFilter.filter_keys.pageVarBools, FieldScope.Page, VarType.Bools);

const elemVarString = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarString
>(segment.DependentEventFilter.filter_keys.elemVarString, FieldScope.Element, VarType.Str);

const elemVarStrings = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarStrings
>(segment.DependentEventFilter.filter_keys.elemVarStrings, FieldScope.Element, VarType.Strs);

const elemVarInt = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarInt
>(segment.DependentEventFilter.filter_keys.elemVarInt, FieldScope.Element, VarType.Int);

const elemVarInts = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarInts
>(segment.DependentEventFilter.filter_keys.elemVarInts, FieldScope.Element, VarType.Ints);

const elemVarFloat = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarFloat
>(segment.DependentEventFilter.filter_keys.elemVarFloat, FieldScope.Element, VarType.Real);

const elemVarFloats = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarFloats
>(segment.DependentEventFilter.filter_keys.elemVarFloats, FieldScope.Element, VarType.Reals);

const elemVarDate = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarDate
>(segment.DependentEventFilter.filter_keys.elemVarDate, FieldScope.Element, VarType.Date);

const elemVarDates = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarDates
>(segment.DependentEventFilter.filter_keys.elemVarDates, FieldScope.Element, VarType.Dates);

const elemVarBool = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarBool
>(segment.DependentEventFilter.filter_keys.elemVarBool, FieldScope.Element, VarType.Bool);

const elemVarBools = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.elemVarBools
>(segment.DependentEventFilter.filter_keys.elemVarBools, FieldScope.Element, VarType.Bools);

const fsevtVarInt = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarInt
>(segment.DependentEventFilter.filter_keys.fsevtVarInt, FieldScope.Fsevt, VarType.Int);

const fsevtVarInts = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarInts
>(segment.DependentEventFilter.filter_keys.fsevtVarInts, FieldScope.Fsevt, VarType.Ints);

const fsevtVarFloat = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarFloat
>(segment.DependentEventFilter.filter_keys.fsevtVarFloat, FieldScope.Fsevt, VarType.Real);

const fsevtVarFloats = customvars.customVarNumbers<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarFloats
>(segment.DependentEventFilter.filter_keys.fsevtVarFloats, FieldScope.Fsevt, VarType.Reals);

const fsevtVarBool = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarBool
>(segment.DependentEventFilter.filter_keys.fsevtVarBool, FieldScope.Fsevt, VarType.Bool);

const fsevtVarBools = customvars.customVarBools<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarBools
>(segment.DependentEventFilter.filter_keys.fsevtVarBools, FieldScope.Fsevt, VarType.Bools);

const fsevtVarString = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarString
>(segment.DependentEventFilter.filter_keys.fsevtVarString, FieldScope.Fsevt, VarType.Str);

const fsevtVarStrings = customvars.customVarString<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarStrings
>(segment.DependentEventFilter.filter_keys.fsevtVarStrings, FieldScope.Fsevt, VarType.Strs);

const fsevtVarDate = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarDate
>(segment.DependentEventFilter.filter_keys.fsevtVarDate, FieldScope.Fsevt, VarType.Date);

const fsevtVarDates = customvars.customVarDates<
  DependentType,
  segment.DependentEventFilter.filter_keys.fsevtVarDates
>(segment.DependentEventFilter.filter_keys.fsevtVarDates, FieldScope.Fsevt, VarType.Dates);

const pageviewTotalDuration = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageviewTotalDuration
>(
  segment.DependentEventFilter.filter_keys.pageviewTotalDuration,
  'EventDurationMillis',
  0,
  DurationUnit.milli,
);

const pageviewActiveDuration = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageviewActiveDuration
>(
  segment.DependentEventFilter.filter_keys.pageviewActiveDuration,
  'EventSecondaryDurationMillis',
  0,
  DurationUnit.milli,
);

const pageviewInactiveDuration = helpers.durationOperatorConverter<
  DependentType,
  segment.DependentEventFilter.filter_keys.pageviewInactiveDuration
>(
  segment.DependentEventFilter.filter_keys.pageviewInactiveDuration,
  'PageViewInactiveDurationMillis',
  0,
  DurationUnit.milli,
);

// DependentConverterMap defines the specification to convert between the UI's
// representation of dependent events and the server's representation.
export const DependentConverterMap: DependentConverterMap = {
  [NONE]: {
    [NONE]: {
      criteria: () => [],
      withoutPartials: () => null,
      extractFromCriteria: () => null,
      extractFromPartialCriteria: () => null,
      searchTypeToFilter: () => null,
      filterToSearchType: sf => ({
        kind: NONE,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
  [segment.DependentEventFilter.filter_keys.start]: dateConverter(
    segment.DependentEventFilter.filter_keys.start,
    'EventStart',
  ),
  [segment.DependentEventFilter.filter_keys.sessionStart]: dateConverter(
    segment.DependentEventFilter.filter_keys.sessionStart,
    'SessionStart',
  ),
  [segment.DependentEventFilter.filter_keys.city]: city,
  [segment.DependentEventFilter.filter_keys.region]: region,
  [segment.DependentEventFilter.filter_keys.country]: country,
  [segment.DependentEventFilter.filter_keys.email]: email,
  [segment.DependentEventFilter.filter_keys.name]: name,
  [segment.DependentEventFilter.filter_keys.signedUp]: {
    [segment.SignedUpOperator.operator_keys.signedUp]: {
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      searchTypeToFilter: st => helpers.searchTypeToFilterMirror(st),
      filterToSearchType: sf => helpers.filterToSearchTypeMirror(sf),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.DependentEventFilter.filter_keys.userId]: userId,
  [segment.DependentEventFilter.filter_keys.userAgent]: userAgent,
  [segment.DependentEventFilter.filter_keys.requestMethod]: requestMethod,
  [segment.DependentEventFilter.filter_keys.statusCode]: statusCode,
  [segment.DependentEventFilter.filter_keys.scrollDepth]: scrollDepth,
  [segment.DependentEventFilter.filter_keys.appViewName]: appViewName,
  [segment.DependentEventFilter.filter_keys.appPackageName]: appPackageName,
  [segment.DependentEventFilter.filter_keys.appName]: appName,
  [segment.DependentEventFilter.filter_keys.deviceModel]: deviceModel,
  [segment.DependentEventFilter.filter_keys.deviceVendor]: deviceVendor,
  [segment.DependentEventFilter.filter_keys.ipAddress]: ipAddress,
  [segment.DependentEventFilter.filter_keys.browser]: {
    [segment.BrowserOperator.operator_keys.equals]: {
      criteria: s => [
        { Field: 'PageBrowser', Op: Op.SEARCH_OP_EQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: Browser[]) => ({
          ok: value.filter(val => val != Browser.any).length > 0,
          value: value.filter(val => val != Browser.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.browser,
          subkind: segment.BrowserOperator.operator_keys
            .equals as segment.BrowserOperator.operator_keys.equals,
          value: maybeWrapValue(c[0].Value) as Browser[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PageBrowser' &&
        c[0].Op == Op.SEARCH_OP_EQ && {
          kind: segment.DependentEventFilter.filter_keys.browser,
          subkind: segment.BrowserOperator.operator_keys
            .equals as segment.BrowserOperator.operator_keys.equals,
          value: [Browser.any],
        },
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.forEachValue(helpers.uiBrowserToProtoBrowser)),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.forEachValue(helpers.protoBrowserToUiBrowser)),
    },
    [segment.BrowserOperator.operator_keys.notEquals]: {
      criteria: s => [
        { Field: 'PageBrowser', Op: Op.SEARCH_OP_NEQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: Browser[]) => ({
          ok: value.filter(val => val != Browser.any).length > 0,
          value: value.filter(val => val != Browser.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.browser,
          subkind: segment.BrowserOperator.operator_keys
            .notEquals as segment.BrowserOperator.operator_keys.notEquals,
          value: maybeWrapValue(c[0].Value) as Browser[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PageBrowser' &&
        c[0].Op == Op.SEARCH_OP_NEQ && {
          kind: segment.DependentEventFilter.filter_keys.browser,
          subkind: segment.BrowserOperator.operator_keys
            .notEquals as segment.BrowserOperator.operator_keys.notEquals,
          value: [Browser.any],
        },
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.forEachValue(helpers.uiBrowserToProtoBrowser)),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.forEachValue(helpers.protoBrowserToUiBrowser)),
    },
  },
  [segment.DependentEventFilter.filter_keys.device]: {
    [segment.DeviceOperator.operator_keys.equals]: {
      criteria: s => [
        { Field: 'PageDevice', Op: Op.SEARCH_OP_EQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: Device[]) => ({
          ok: value.filter(val => val != Device.any).length > 0,
          value: value.filter(val => val != Device.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.device,
          subkind: segment.DeviceOperator.operator_keys.equals,
          value: maybeWrapValue(c[0].Value) as Device[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PageDevice' &&
        c[0].Op == Op.SEARCH_OP_EQ && {
          kind: segment.DependentEventFilter.filter_keys.device,
          subkind: segment.DeviceOperator.operator_keys.equals,
          value: [Device.any],
        },
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.forEachValue(helpers.uiDeviceToProtoDevice)),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.forEachValue(helpers.protoDeviceToUiDevice)),
    },
    [segment.DeviceOperator.operator_keys.notEquals]: {
      criteria: s => [
        { Field: 'PageDevice', Op: Op.SEARCH_OP_NEQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: Device[]) => ({
          ok: value.filter(val => val != Device.any).length > 0,
          value: value.filter(val => val != Device.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.device,
          subkind: segment.DeviceOperator.operator_keys.notEquals,
          value: maybeWrapValue(c[0].Value) as Device[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PageDevice' &&
        c[0].Op == Op.SEARCH_OP_NEQ && {
          kind: segment.DependentEventFilter.filter_keys.device,
          subkind: segment.DeviceOperator.operator_keys.notEquals,
          value: [Device.any],
        },
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.forEachValue(helpers.uiDeviceToProtoDevice)),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.forEachValue(helpers.protoDeviceToUiDevice)),
    },
  },
  [segment.DependentEventFilter.filter_keys.platform]: {
    [segment.PlatformOperator.operator_keys.equals]: {
      criteria: s => [
        { Field: 'PagePlatform', Op: Op.SEARCH_OP_EQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: Platform[]) => ({
          ok: value.filter(val => val != Platform.any).length > 0,
          value: value.filter(val => val != Platform.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.platform,
          subkind: segment.PlatformOperator.operator_keys.equals,
          value: maybeWrapValue(c[0].Value) as Platform[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PagePlatform' &&
        c[0].Op == Op.SEARCH_OP_EQ && {
          kind: segment.DependentEventFilter.filter_keys.platform,
          subkind: segment.PlatformOperator.operator_keys.equals,
          value: [Platform.any],
        },
      searchTypeToFilter: st =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(helpers.uiPlatformToProtoPlatform),
        ),
      filterToSearchType: sf =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(helpers.protoPlatformToUiPlatform),
        ),
    },
    [segment.PlatformOperator.operator_keys.notEquals]: {
      criteria: s => [
        { Field: 'PagePlatform', Op: Op.SEARCH_OP_NEQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: Platform[]) => ({
          ok: value.filter(val => val != Platform.any).length > 0,
          value: value.filter(val => val != Platform.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.platform,
          subkind: segment.PlatformOperator.operator_keys.notEquals,
          value: maybeWrapValue(c[0].Value) as Platform[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PagePlatform' &&
        c[0].Op == Op.SEARCH_OP_NEQ && {
          kind: segment.DependentEventFilter.filter_keys.platform,
          subkind: segment.PlatformOperator.operator_keys.notEquals,
          value: [Platform.any],
        },
      searchTypeToFilter: st =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(helpers.uiPlatformToProtoPlatform),
        ),
      filterToSearchType: sf =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(helpers.protoPlatformToUiPlatform),
        ),
    },
  },
  [segment.DependentEventFilter.filter_keys.os]: {
    [segment.OSOperator.operator_keys.equals]: {
      criteria: s => [
        { Field: 'PageOperatingSystem', Op: Op.SEARCH_OP_EQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: OS[]) => ({
          ok: value.filter(val => val != OS.any).length > 0,
          value: value.filter(val => val != OS.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.os,
          subkind: segment.OSOperator.operator_keys.equals,
          value: maybeWrapValue(c[0].Value) as OS[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PageOperatingSystem' &&
        c[0].Op == Op.SEARCH_OP_EQ && {
          kind: segment.DependentEventFilter.filter_keys.os,
          subkind: segment.OSOperator.operator_keys.equals,
          value: [OS.any],
        },
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.forEachValue(helpers.uiOsToProtoOs)),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.forEachValue(helpers.protoOsToUiOs)),
    },
    [segment.OSOperator.operator_keys.notEquals]: {
      criteria: s => [
        { Field: 'PageOperatingSystem', Op: Op.SEARCH_OP_NEQ, Value: maybeUnwrapValue(s.value) },
      ],
      withoutPartials: s =>
        helpers.stripPartials(s, (value: OS[]) => ({
          ok: value.filter(val => val != OS.any).length > 0,
          value: value.filter(val => val != OS.any),
        })),
      extractFromCriteria: c =>
        c[0] && {
          kind: segment.DependentEventFilter.filter_keys.os,
          subkind: segment.OSOperator.operator_keys.notEquals,
          value: maybeWrapValue(c[0].Value) as OS[],
        },
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'PageOperatingSystem' &&
        c[0].Op == Op.SEARCH_OP_NEQ && {
          kind: segment.DependentEventFilter.filter_keys.os,
          subkind: segment.OSOperator.operator_keys.notEquals,
          value: [OS.any],
        },
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.forEachValue(helpers.uiOsToProtoOs)),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.forEachValue(helpers.protoOsToUiOs)),
    },
  },
  [segment.DependentEventFilter.filter_keys.sessionType]: helpers.sessionTypeOperatorConverter(),
  [segment.DependentEventFilter.filter_keys.pageName]: pageName,
  [segment.DependentEventFilter.filter_keys.pageDuration]: pageDuration,
  [segment.DependentEventFilter.filter_keys.pageActiveDuration]: pageActiveDuration,
  [segment.DependentEventFilter.filter_keys.timeSincePageLoad]: timeSincePageLoad,
  [segment.DependentEventFilter.filter_keys.timeSinceSessionStart]: timeSinceSessionStart,
  [segment.DependentEventFilter.filter_keys.timeDurationRendered]: timeDurationRendered,
  [segment.DependentEventFilter.filter_keys.timeDurationVisible]: timeDurationVisible,
  [segment.DependentEventFilter.filter_keys.consoleErrorsOnPage]: consoleErrorsOnPage,
  [segment.DependentEventFilter.filter_keys.consoleWarningsOnPage]: consoleWarningsOnPage,
  [segment.DependentEventFilter.filter_keys.displayWidth]: displayWidth,
  [segment.DependentEventFilter.filter_keys.displayHeight]: displayHeight,
  [segment.DependentEventFilter.filter_keys.viewportWidth]: viewportWidth,
  [segment.DependentEventFilter.filter_keys.viewportHeight]: viewportHeight,
  [segment.DependentEventFilter.filter_keys.referrerUrl]: referrerUrl,
  [segment.DependentEventFilter.filter_keys.url]: url,
  [segment.DependentEventFilter.filter_keys.ajaxRequestUrl]: ajaxRequestUrl,
  [segment.DependentEventFilter.filter_keys.webSourceFileUrl]: webSourceFileUrl,
  [segment.DependentEventFilter.filter_keys.mobileSourceFile]: mobileSourceFile,
  [segment.DependentEventFilter.filter_keys.appVersion]: appVersion,
  [segment.DependentEventFilter.filter_keys.appOsVersion]: appOsVersion,
  [segment.DependentEventFilter.filter_keys.appFsVersion]: appFsVersion,
  [segment.DependentEventFilter.filter_keys.browserVersion]: browserVersion,
  [segment.DependentEventFilter.filter_keys.text]: elementLikeConverter(
    segment.DependentEventFilter.filter_keys.text,
    {
      includes: [
        segment.ElementOperator.operator_keys.text,
        segment.ElementOperator.operator_keys.textIsNot,
        segment.ElementOperator.operator_keys.textStartsWith,
        segment.ElementOperator.operator_keys.textEndsWith,
        segment.ElementOperator.operator_keys.textContainsToken,
        segment.ElementOperator.operator_keys.textNotContainsToken,
        segment.ElementOperator.operator_keys.textContainsTokenStartsWith,
      ],
      renames: new Map([
        [segment.ElementOperator.operator_keys.text, segment.TextOperator.operator_keys.equals],
        [
          segment.ElementOperator.operator_keys.textIsNot,
          segment.TextOperator.operator_keys.notEquals,
        ],
        [
          segment.ElementOperator.operator_keys.textStartsWith,
          segment.TextOperator.operator_keys.startsWith,
        ],
        [
          segment.ElementOperator.operator_keys.textEndsWith,
          segment.TextOperator.operator_keys.endsWith,
        ],
        [
          segment.ElementOperator.operator_keys.textContainsToken,
          segment.TextOperator.operator_keys.containsToken,
        ],
        [
          segment.ElementOperator.operator_keys.textNotContainsToken,
          segment.TextOperator.operator_keys.notContainsToken,
        ],
        [
          segment.ElementOperator.operator_keys.textContainsTokenStartsWith,
          segment.TextOperator.operator_keys.containsTokenStartsWith,
        ],
      ]),
    },
  ),
  [segment.DependentEventFilter.filter_keys.css]: elementLikeConverter(
    segment.DependentEventFilter.filter_keys.css,
    {
      includes: [
        segment.ElementOperator.operator_keys.css,
        segment.ElementOperator.operator_keys.cssIsNot,
      ],
      renames: new Map([
        [segment.ElementOperator.operator_keys.css, segment.CssOperator.operator_keys.matches],
        [
          segment.ElementOperator.operator_keys.cssIsNot,
          segment.CssOperator.operator_keys.notMatches,
        ],
      ]),
    },
  ),
  [segment.DependentEventFilter.filter_keys.element]: elementLikeConverter(
    segment.DependentEventFilter.filter_keys.element,
    {
      includes: [segment.ElementOperator.operator_keys.withElementId],
    },
  ),
  [segment.DependentEventFilter.filter_keys.elemVarString]: elemVarString,
  [segment.DependentEventFilter.filter_keys.elemVarStrings]: elemVarStrings,
  [segment.DependentEventFilter.filter_keys.elemVarInt]: elemVarInt,
  [segment.DependentEventFilter.filter_keys.elemVarInts]: elemVarInts,
  [segment.DependentEventFilter.filter_keys.elemVarFloat]: elemVarFloat,
  [segment.DependentEventFilter.filter_keys.elemVarFloats]: elemVarFloats,
  [segment.DependentEventFilter.filter_keys.elemVarDate]: elemVarDate,
  [segment.DependentEventFilter.filter_keys.elemVarDates]: elemVarDates,
  [segment.DependentEventFilter.filter_keys.elemVarBool]: elemVarBool,
  [segment.DependentEventFilter.filter_keys.elemVarBools]: elemVarBools,
  [segment.DependentEventFilter.filter_keys.pageDefId]: pageDefId,
  [segment.DependentEventFilter.filter_keys.prevPageDefId]: prevPageDefId,
  [segment.DependentEventFilter.filter_keys.sessionEntryPage]: sessionEntryPage,
  [segment.DependentEventFilter.filter_keys.sessionExitPage]: sessionExitPage,
  [segment.DependentEventFilter.filter_keys.customEventPropertyString]: customEventPropertyString,
  [segment.DependentEventFilter.filter_keys.customEventPropertyStrings]: customEventPropertyStrings,
  [segment.DependentEventFilter.filter_keys.customEventPropertyInt]: customEventPropertyInt,
  [segment.DependentEventFilter.filter_keys.customEventPropertyInts]: customEventPropertyInts,
  [segment.DependentEventFilter.filter_keys.customEventPropertyFloat]: customEventPropertyFloat,
  [segment.DependentEventFilter.filter_keys.customEventPropertyFloats]: customEventPropertyFloats,
  [segment.DependentEventFilter.filter_keys.customEventPropertyDate]: customEventPropertyDate,
  [segment.DependentEventFilter.filter_keys.customEventPropertyDates]: customEventPropertyDates,
  [segment.DependentEventFilter.filter_keys.customEventPropertyBool]: customEventPropertyBool,
  [segment.DependentEventFilter.filter_keys.customEventPropertyBools]: customEventPropertyBools,
  [segment.DependentEventFilter.filter_keys.userVarString]: userVarString,
  [segment.DependentEventFilter.filter_keys.userVarStrings]: userVarStrings,
  [segment.DependentEventFilter.filter_keys.userVarInt]: userVarInt,
  [segment.DependentEventFilter.filter_keys.userVarInts]: userVarInts,
  [segment.DependentEventFilter.filter_keys.userVarFloat]: userVarFloat,
  [segment.DependentEventFilter.filter_keys.userVarFloats]: userVarFloats,
  [segment.DependentEventFilter.filter_keys.userVarDate]: userVarDate,
  [segment.DependentEventFilter.filter_keys.userVarDates]: userVarDates,
  [segment.DependentEventFilter.filter_keys.userVarBool]: userVarBool,
  [segment.DependentEventFilter.filter_keys.userVarBools]: userVarBools,
  [segment.DependentEventFilter.filter_keys.customApiErrorProperty]: customApiErrorProperty,
  [segment.DependentEventFilter.filter_keys.fsUserId]: fsUserId,
  [segment.DependentEventFilter.filter_keys.fsPageClusterId]: fsPageClusterId,
  [segment.DependentEventFilter.filter_keys.pageVarString]: pageVarString,
  [segment.DependentEventFilter.filter_keys.pageVarStrings]: pageVarStrings,
  [segment.DependentEventFilter.filter_keys.pageVarInt]: pageVarInt,
  [segment.DependentEventFilter.filter_keys.pageVarInts]: pageVarInts,
  [segment.DependentEventFilter.filter_keys.pageVarFloat]: pageVarFloat,
  [segment.DependentEventFilter.filter_keys.pageVarFloats]: pageVarFloats,
  [segment.DependentEventFilter.filter_keys.pageVarDate]: pageVarDate,
  [segment.DependentEventFilter.filter_keys.pageVarDates]: pageVarDates,
  [segment.DependentEventFilter.filter_keys.pageVarBool]: pageVarBool,
  [segment.DependentEventFilter.filter_keys.pageVarBools]: pageVarBools,
  [segment.DependentEventFilter.filter_keys.fsevtVarString]: fsevtVarString,
  [segment.DependentEventFilter.filter_keys.fsevtVarStrings]: fsevtVarStrings,
  [segment.DependentEventFilter.filter_keys.fsevtVarInt]: fsevtVarInt,
  [segment.DependentEventFilter.filter_keys.fsevtVarInts]: fsevtVarInts,
  [segment.DependentEventFilter.filter_keys.fsevtVarFloat]: fsevtVarFloat,
  [segment.DependentEventFilter.filter_keys.fsevtVarFloats]: fsevtVarFloats,
  [segment.DependentEventFilter.filter_keys.fsevtVarDate]: fsevtVarDate,
  [segment.DependentEventFilter.filter_keys.fsevtVarDates]: fsevtVarDates,
  [segment.DependentEventFilter.filter_keys.fsevtVarBool]: fsevtVarBool,
  [segment.DependentEventFilter.filter_keys.fsevtVarBools]: fsevtVarBools,
  [segment.DependentEventFilter.filter_keys.pinchGestureType]: {
    [segment.PinchGestureOperator.operator_keys.equals]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: PinchGesture) => ({
          ok: true,
          value,
        })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.uiPinchGestureToProtoPinchGesture),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.protoPinchGestureToUiPinchGesture),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.DependentEventFilter.filter_keys.seenVisibility]: {
    [segment.SeenVisibilityOperator.operator_keys.equals]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: SeenVisibility) => ({
          ok: true,
          value,
        })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(st, helpers.uiSeenVisibilityToProtoSeenVisibility),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(sf, helpers.protoSeenVisibilityToUiSeenVisibility),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.DependentEventFilter.filter_keys.errorClickType]: {
    [segment.ErrorClickTypeOperator.operator_keys.equals]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: ErrorClickType[]) => ({
          ok: value.filter(val => val != ErrorClickType.any).length > 0,
          value: value.filter(val => val != ErrorClickType.any),
        })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(helpers.uiErrorClickTypeToProtoErrorClickType),
        ),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(helpers.protoErrorClickTypeToUiErrorClickType),
        ),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
    [segment.ErrorClickTypeOperator.operator_keys.notEquals]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: ErrorClickType[]) => ({
          ok: value.filter(val => val != ErrorClickType.any).length > 0,
          value: value.filter(val => val != ErrorClickType.any),
        })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(helpers.uiErrorClickTypeToProtoErrorClickType),
        ),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(helpers.protoErrorClickTypeToUiErrorClickType),
        ),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.DependentEventFilter.filter_keys.datalossType]: {
    [segment.DatalossTypeOperator.operator_keys.equals]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: DatalossType[]) => ({
          ok: value.length > 0,
          value,
        })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(helpers.uiDatalossTypeToProtoDatalossType),
        ),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(helpers.protoDatalossTypeToUiDatalossType),
        ),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
    [segment.DatalossTypeOperator.operator_keys.notEquals]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: DatalossType[]) => ({
          ok: value.length > 0,
          value,
        })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(helpers.uiDatalossTypeToProtoDatalossType),
        ),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(helpers.protoDatalossTypeToUiDatalossType),
        ),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.DependentEventFilter.filter_keys.datalossOrigin]: {
    [segment.DatalossOriginOperator.operator_keys.startsWith]: {
      withoutPartials: s =>
        helpers.stripPartials(s, (value: string[]) => ({ ok: value.length > 0, value })),
      searchTypeToFilter: (st): SearchFilter =>
        helpers.searchTypeToFilterMirror(
          st,
          helpers.forEachValue(s => s),
        ),
      filterToSearchType: (sf: SearchFilter): SearchType =>
        helpers.filterToSearchTypeMirror(
          sf,
          helpers.forEachValue(s => s),
        ),
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.DependentEventFilter.filter_keys.pageviewTotalDuration]: pageviewTotalDuration,
  [segment.DependentEventFilter.filter_keys.pageviewActiveDuration]: pageviewActiveDuration,
  [segment.DependentEventFilter.filter_keys.pageviewInactiveDuration]: pageviewInactiveDuration,
  [UiOnlyKinds.threshold]: {
    [UiOnlySubkinds.thresholdIs]: {
      withoutPartials: s => ({ ok: false, s }),
      searchTypeToFilter: (st): SearchFilter => {
        throw new Error('synthetic type attempted to be converted');
      },
      filterToSearchType: (sf: SearchFilter): SearchType => {
        throw new Error('synthetic type attempted to be converted');
      },
      criteria: s => null,
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
};
